import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Initialize hide unavailable toggle if enabled
    this.#initializeHideUnavailable();

    // Observe grid for new products added via infinite scroll
    this.#observeGridForNewProducts();
  }

  /**
   * Initialize hide unavailable toggle if enabled
   */
  #initializeHideUnavailable() {
    const wrapper = this.querySelector('.collection-wrapper[data-hide-unavailable-enabled="true"]');
    if (!wrapper) return;

    const toggleButton = wrapper.querySelector('.collection-toggle-button');
    if (!toggleButton) return;

    toggleButton.addEventListener('click', this.toggleHideUnavailable.bind(this));
    
    // Check localStorage for saved preference, default to hidden (true)
    const collectionId = wrapper.dataset.collectionId;
    const savedPreference = localStorage.getItem(`hideUnavailableCollection_${collectionId}`);
    const shouldHide = savedPreference === null ? true : savedPreference === 'true';
    
    if (shouldHide) {
      this.hideUnavailableProducts(wrapper, true);
      toggleButton.dataset.hideUnavailable = 'true';
      toggleButton.setAttribute('aria-pressed', 'true');
    } else {
      this.hideUnavailableProducts(wrapper, false);
      toggleButton.dataset.hideUnavailable = 'false';
      toggleButton.setAttribute('aria-pressed', 'false');
    }
  }

  /**
   * Observe grid for new products added via infinite scroll or pagination
   */
  #observeGridForNewProducts() {
    const wrapper = this.querySelector('.collection-wrapper[data-hide-unavailable-enabled="true"]');
    if (!wrapper) return;

    const { grid } = this.refs;
    if (!grid) return;

    const observer = new MutationObserver(() => {
      const toggleButton = wrapper.querySelector('.collection-toggle-button');
      if (toggleButton && toggleButton.dataset.hideUnavailable === 'true') {
        // Re-apply hide state to newly loaded products
        this.hideUnavailableProducts(wrapper, true);
      }
    });

    observer.observe(grid, {
      childList: true,
      subtree: false,
    });
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Toggles the hide unavailable products feature.
   */
  toggleHideUnavailable(event) {
    const wrapper = event.currentTarget.closest('.collection-wrapper[data-hide-unavailable-enabled="true"]');
    if (!wrapper) return;

    const toggleButton = wrapper.querySelector('.collection-toggle-button');
    if (!toggleButton) return;

    const currentState = toggleButton.dataset.hideUnavailable === 'true';
    const newState = !currentState;

    this.hideUnavailableProducts(wrapper, newState);
    toggleButton.dataset.hideUnavailable = newState.toString();
    toggleButton.setAttribute('aria-pressed', newState.toString());

    // Save preference to localStorage
    const collectionId = wrapper.dataset.collectionId;
    if (collectionId) {
      localStorage.setItem(`hideUnavailableCollection_${collectionId}`, newState.toString());
    }
  }

  /**
   * Hides or shows unavailable products.
   * @param {HTMLElement} wrapper - The collection wrapper element.
   * @param {boolean} hide - Whether to hide unavailable products.
   */
  hideUnavailableProducts(wrapper, hide) {
    const unavailableItems = wrapper.querySelectorAll(
      '.product-grid__item[data-product-unavailable="true"]'
    );
    unavailableItems.forEach((item) => {
      if (hide) {
        item.classList.add('product-grid__item--hidden');
      } else {
        item.classList.remove('product-grid__item--hidden');
      }
    });

    // Update toggle button text
    const toggleButton = wrapper.querySelector('.collection-toggle-button');
    const toggleText = toggleButton?.querySelector('.collection-toggle-text');
    if (toggleText) {
      toggleText.textContent = hide
        ? (toggleButton.dataset.showText || toggleText.textContent)
        : (toggleButton.dataset.hideText || toggleText.textContent);
    }
    if (toggleButton) {
      toggleButton.setAttribute('aria-label', hide
        ? (toggleButton.dataset.showText || '')
        : (toggleButton.dataset.hideText || ''));
    }
  }

}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
